import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Observable, EMPTY, from, isObservable, of } from 'rxjs';
import { catchError, filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Actions } from '@ngrx/effects';

type ActionTypeLike = string | { type: string } | ((...args: any[]) => any);

interface FetchOptions<TState, TAction extends Action = Action> {
  run(action: TAction, state: TState): Observable<Action> | Action | Action[] | void;
  onError?(action: TAction, error: any): Observable<Action> | Action | Action[] | void;
}

@Injectable({ providedIn: 'root' })
export class DataPersistence<TState = any> {
  constructor(private actions$: Actions, private store: Store<TState>) {}

  fetch<TAction extends Action = Action>(
    actionType: ActionTypeLike,
    opts: FetchOptions<TState, TAction>
  ): Observable<Action> {
    const type = this.resolveType(actionType);

    return this.actions$.pipe(
      filter((action: Action) => action.type === type),
      withLatestFrom(this.store as Observable<TState>),
      mergeMap(([action, state]) => {
        try {
          return this.toActionStream(opts.run(action as TAction, state)).pipe(
            catchError((error) => this.toActionStream(opts.onError?.(action as TAction, error)))
          );
        } catch (error) {
          return this.toActionStream(opts.onError?.(action as TAction, error));
        }
      })
    );
  }

  private resolveType(actionType: ActionTypeLike): string {
    if (typeof actionType === 'string') {
      return actionType;
    }

    if (typeof actionType === 'function' && (actionType as any).type) {
      return (actionType as any).type;
    }

    return (actionType as any).type;
  }

  private toActionStream(result: Observable<Action> | Action | Action[] | void): Observable<Action> {
    if (result == null) {
      return EMPTY;
    }

    if (isObservable(result)) {
      return (result as Observable<Action | Action[]>).pipe(mergeMap((value) => this.toActionStream(value)));
    }

    if (Array.isArray(result)) {
      return from(result as Action[]);
    }

    return of(result as Action);
  }
}
